/** The save browser: named slots, the autosave, and the two file doors.
 *
 * The format, the migrations, the slot index and the file download were all
 * finished and tested with **zero callers**. This is the screen that calls
 * them. It is one component in two modes rather than two screens, because a
 * player who opens "Save" and finds the game he meant to overwrite is looking
 * at the same list a player who opened "Load" is looking at, and the moment the
 * two lists are built by two components they start disagreeing.
 *
 * It holds no storage knowledge at all. The host hands it rows and takes back
 * `onSave(slot)` / `onLoad(slot)` / `onDelete(slot)`, so the only place that
 * knows what a browser slot is remains `apps/web/src/game/persist.ts` (I11).
 *
 * The timestamp is formatted here from the parts rather than through
 * `toLocaleString`, so two runs of the screenshot harness in two locales
 * produce the same pixels.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { BitmapText } from "../theme/bitmapText.js";
import { usePolishStyles } from "./polish.js";
import { Icon, ScreenStyles } from "./primitives.js";
import { FORM_ART_CSS, PaintedPlate, crestKey } from "./formArt.js";
import { StyleLayer, useStyleSheet } from "../theme/styleSheet.js";

/** One row of the save index. Structurally `SlotInfo` from the host's
 *  `persist.ts`, restated here so this package does not import the app. */
export interface SaveSlotRow {
  readonly slot: string;
  readonly day: number;
  readonly week: number;
  readonly month: number;
  /** Milliseconds since the epoch, from the host's clock. Not a game fact. */
  readonly savedAt: number;
  readonly bytes: number;
  /**
   * The faction the saved game was played as, for the slot's crest.
   *
   * Optional because the slot index does not carry it yet: `persist.ts` writes
   * slot, day, week, month, savedAt and bytes, and the day plate below is what
   * a row shows without it. It is declared here rather than left out so the
   * host has one obvious field to fill - a save list where every row wears the
   * keep it was played from is what the classic's own load dialog shows, and every
   * one of the twelve keeps is already packed.
   */
  readonly faction?: string | null | undefined;
}

export interface SaveLoadProps {
  readonly mode: "save" | "load";
  readonly slots: readonly SaveSlotRow[];
  /** The slot the game writes by itself. Named so the list can say so, and so
   *  the save form can refuse to let a player type over it by hand. */
  readonly autosaveSlot: string;
  /** False at the main menu: there is no live game to write down. */
  readonly canSave: boolean;
  /** The day the live game is on, for the default save name. */
  readonly day?: number | undefined;
  /** The last outcome, good or bad. The host owns the words. */
  readonly message?: string | null | undefined;
  readonly onSave: (slot: string) => void;
  readonly onLoad: (slot: string) => void;
  readonly onDelete: (slot: string) => void;
  readonly onSaveToFile: () => void;
  readonly onLoadFromFile: (file: File) => void;
  readonly onClose: () => void;
}

/** A slot name a player typed, normalised.
 *
 * The storage key is `"realm.save.v1:" + slot`, so no name a player can type
 * collides with the index's own key however it is spelled; all this has to do
 * is collapse the whitespace - two names differing by a double space are one
 * save to the person reading the list - and cap the length so the list stays
 * legible. */
export function cleanSlotName(raw: string): string {
  return raw.replace(/\s+/g, " ").trim().slice(0, 40);
}

/** `2026-08-27 14:32`. Sortable, unambiguous, and the same in every locale. */
export function stampText(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "unknown";
  const d = new Date(ms);
  const p = (n: number, w = 2): string => String(n).padStart(w, "0");
  return `${p(d.getFullYear(), 4)}-${p(d.getMonth() + 1)}-${p(d.getDate())} `
    + `${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Bytes as something a player can weigh against a 5 MB origin quota. */
export function sizeText(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1048576) return `${String(Math.round(bytes / 1024))} KiB`;
  return `${(bytes / 1048576).toFixed(1)} MiB`;
}

/** Month 1 week 1 day 3 is "Day 3"; the kernel counts days from the start of
 *  the week, so the running day has to be reconstructed the way the classic shows
 *  it - month, week, day - rather than as one number nobody can place. */
export function whenText(row: SaveSlotRow): string {
  return `Month ${String(row.month)}, Week ${String(row.week)}, Day ${String(row.day)}`;
}

function byNewest(a: SaveSlotRow, b: SaveSlotRow): number {
  return b.savedAt - a.savedAt || (a.slot < b.slot ? -1 : a.slot > b.slot ? 1 : 0);
}

export function SaveLoad(props: SaveLoadProps): JSX.Element {
  usePolishStyles();
  const { mode, slots, autosaveSlot, canSave } = props;

  const rows = useMemo(() => [...slots].sort(byNewest), [slots]);
  const suggestion = props.day === undefined ? "Save" : `Day ${String(props.day)}`;
  const [name, setName] = useState(suggestion);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  // The default name follows the game: opening Save on day 12 after saving on
  // day 6 should not offer "Day 6" again. Only the untouched field moves.
  const lastSuggestion = useRef(suggestion);
  useEffect(() => {
    if (lastSuggestion.current === suggestion) return;
    setName((n) => (n === lastSuggestion.current ? suggestion : n));
    lastSuggestion.current = suggestion;
  }, [suggestion]);

  const slot = cleanSlotName(name);
  const isAuto = slot === autosaveSlot;
  const overwrites = rows.some((r) => r.slot === slot);
  const saveWhy = !canSave
    ? "There is no game running to save."
    : slot === ""
      ? "Give the save a name."
      : isAuto
        ? `"${autosaveSlot}" is written by the game itself; pick another name.`
        : null;

  const title = mode === "save" ? "Save Game" : "Load Game";

  return (
    <div className="hs-screen hs-screen--sheet hm-sl" role="region" aria-label={title}>
      <ScreenStyles />
      <SaveLoadStyles />

      {/* The veil is OPAQUE, and that is a reversal.
          It was `backdrop-filter: brightness(0.52)`, chosen because a 0.55
          black scrim lifts the ground as fast as it lowers a gilt letterform
          and multiplying does not. Both are the same idea - dim the title
          screen until its type stops competing - and an art director looking at
          the result read the answer as neither: "BANNERS OF THE REALM" was
          still legible across the frame at metre height, and metre-high
          illegible letterforms behind a dialog are worse than emptiness. A
          door does not stand on a ghost of the room it was opened from. So the
          field behind this screen is flat and dark, and the picture this screen
          was missing is IN it - the plate beside the list - rather than smeared
          under it. */}
      <span className="hm-sl__veil" aria-hidden="true" />

      <div className="hs-screen__bar hs-plank">
        <span className="hs-screen__title hm-cap">{title}</span>
        <span className="hs-screen__sub hs-muted">
          {mode === "save" ? "Slots live in this browser" : "A slot, or a file from disk"}
        </span>
        <span className="hs-spacer" />
        {mode === "save" ? (
          <button
            type="button"
            className="hs-plate hs-plate--sm"
            disabled={!canSave}
            title={canSave ? "Download this game as a file" : "There is no game running to save."}
            onClick={props.onSaveToFile}
          >
            Save to File
          </button>
        ) : (
          <button
            type="button"
            className="hs-plate hs-plate--sm"
            onClick={() => fileRef.current?.click()}
          >
            Load from File
          </button>
        )}
        <button type="button" className="hs-plate hs-plate--sm" onClick={props.onClose}>Close</button>
      </div>

      {/* The picker is the only way a file reaches this screen; it is hidden
          because a bare file input in the middle of a stone panel is the one
          control on the screen a browser paints itself. */}
      <input
        ref={fileRef}
        type="file"
        accept=".json,application/json"
        className="hs-sr"
        aria-label="Choose a save file"
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Cleared first: picking the same file twice in a row fires no
          // change event otherwise, and a failed load is exactly when a player
          // tries the same file again.
          e.target.value = "";
          if (file !== undefined) props.onLoadFromFile(file);
        }}
      />

      {/* The screen IS the picture, and the dialog stands on it.
          the classic opens its load dialog over the painted screen it was
          reached from and never over a gradient; what this build did instead
          was smear the title screen behind the panel at about 8% and call the
          smear a backdrop. So the body holds one plate - a painting the build
          already ships twelve of, cropped to the frame rather than letterboxed
          into it - and the register and its note are set on it. */}
      <div className="hs-screen__body" style={{ justifyContent: "center", padding: "14px" }}>
        <PaintedPlate
          className="hm-sl__stage"
          atlasKey={mode === "save" ? "town.saltreach.background" : "town.greenmantle.background"}
          focusY={0.40}
        >
        <div className="hm-sl__col">
          {mode === "save" ? (
            <div className="hs-card hs-wood">
              <div className="hs-card__title">New Save</div>
              <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                <input
                  className="hs-input"
                  value={name}
                  maxLength={40}
                  aria-label="Save name"
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && saveWhy === null) props.onSave(slot);
                  }}
                />
                <button
                  type="button"
                  className="hs-btn hs-btn--gold"
                  disabled={saveWhy !== null}
                  title={saveWhy ?? (overwrites ? `Overwrite "${slot}"` : `Save as "${slot}"`)}
                  onClick={() => props.onSave(slot)}
                >
                  {overwrites ? "Overwrite" : "Save"}
                </button>
              </div>
              {saveWhy !== null ? (
                <div className="hs-parch hs-why">{saveWhy}</div>
              ) : (
                <div className="hs-tiny hs-muted" style={{ marginTop: "6px" }}>
                  A whole map is about a megabyte and a browser gives the page five.
                  Keep a couple of slots, and use Save to File for the rest.
                </div>
              )}
            </div>
          ) : null}

          {/* The list and the picture stand side by side, which is what makes
              this screen stop being one brown box with a row in it. With a
              single save in the index the list occupied 40px of a 700px panel
              and the other 660px was a gradient - the largest single flat area
              in the build. The panel is now sized by its rows and the room it
              does not want goes to the plate, so there is no leftover for a
              hole to be made of. */}
          <div className="hm-sl__spread">
          {/* The box model is in SAVELOAD_CSS, not here: an inline
              `minHeight: 0` beats any stylesheet, and it was quietly cancelling
              the height this panel is supposed to have. */}
          <div className={`hs-card hs-wood hm-sl__list ${rows.length === 0 ? "hm-sl__list--empty" : ""}`}>
            <div className="hs-card__title">
              {rows.length === 0 ? "No Saved Games" : `Saved Games (${String(rows.length)})`}
            </div>

            {rows.length === 0 ? (
              /* A register with nothing entered in it, RULED.
                 What was here was a 380x60 parchment note floating in a 1000x700
                 wooden box: 58.1% of the 1920x1080 frame measured flat by the
                 32x32 scan, and the box read as a panel that had failed to load
                 rather than as a door with nothing behind it. The journal and
                 the hall of fame already answer this - an empty page is ruled,
                 and the ruling is drawn on the bands above and below the writing
                 so no rule ever crosses a line of type. */
              <div className="hm-sl__well">
                <span className="hm-sl__rule hm-sl__rule--over" aria-hidden="true" />
                {/* The writing is a card slipped into the register rather than
                    ink floating between two ruled bands: the clear strip a
                    paragraph needs is 160px tall and as wide as the panel, and
                    a strip of untouched panel is the defect being fixed, not a
                    smaller version of the fix. */}
                <div className="hm-sl__note">
                  <div className="hm-sl__emptyhead">
                    {mode === "save" ? "No slot is written yet" : "Nothing has been saved yet"}
                  </div>
                  <p className="hm-sl__emptybody">
                    Start a game and the autosave writes itself at the top of every one
                    of your turns. A slot you name yourself is kept beside it, and both
                    live in this browser rather than on disk.
                  </p>
                  <p className="hm-sl__emptybody hm-sl__emptybody--second">
                    {mode === "save"
                      ? "Save to File writes the whole game out as one file, which is the copy that survives a cleared browser."
                      : "Load from File takes a game you wrote out earlier, which is the copy that survives a cleared browser."}
                  </p>
                </div>
                <span className="hm-sl__rule hm-sl__rule--under" aria-hidden="true" />
              </div>
            ) : (
              <div className="hs-list hs-scroll">
                {rows.map((row) => {
                  const auto = row.slot === autosaveSlot;
                  const armed = pendingDelete === row.slot;
                  return (
                    <div key={row.slot} style={{ display: "flex", gap: "6px", alignItems: "stretch" }}>
                      <button
                        type="button"
                        className="hs-listrow"
                        title={mode === "save" ? `Overwrite "${row.slot}"` : `Load "${row.slot}"`}
                        onClick={() => {
                          setPendingDelete(null);
                          if (mode === "load") { props.onLoad(row.slot); return; }
                          // A row in Save mode fills the name rather than
                          // writing: overwriting a save on one click, with no
                          // confirmation, is not a convenience.
                          setName(row.slot);
                        }}
                      >
                        {/* The slot's own plate. A row of type with a size on
                            the end of it is a directory listing; a saved game
                            is a place and a date, so the date is struck in the
                            face this build counts in and the keep is drawn
                            beside it when the index knows which one it was. */}
                        <span className="hm-sl__seal" aria-hidden="true">
                          {row.faction === null || row.faction === undefined ? null : (
                            <Icon
                              atlasKey={crestKey(row.faction)}
                              size={58}
                              boxHeight={52}
                              className="hm-sl__crest"
                            />
                          )}
                          <span className="hm-sl__sealday">
                            <BitmapText scale={2} keyline="#fff6d8">{String(row.day)}</BitmapText>
                          </span>
                          <span className="hm-sl__sealword">
                            {`M${String(row.month)} W${String(row.week)}`}
                          </span>
                        </span>
                        <span style={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
                          <span className="hs-listrow__name hs-clip">
                            {row.slot}
                            {auto ? <span className="hs-tiny hs-muted"> · autosave</span> : null}
                          </span>
                          <span className="hs-listrow__meta">
                            {whenText(row)} · {stampText(row.savedAt)} · <span className="hs-num">{sizeText(row.bytes)}</span>
                          </span>
                        </span>
                        <span className="hs-spacer" />
                        <span className="hs-tiny" style={{ color: "#d9b25a" }}>
                          {mode === "save" ? "Use name" : "Load"}
                        </span>
                      </button>
                      <button
                        type="button"
                        className={armed ? "hs-btn hs-btn--gold" : "hs-btn hs-btn--ghost"}
                        title={armed ? `Delete "${row.slot}" for good` : `Delete "${row.slot}"`}
                        onClick={() => {
                          if (!armed) { setPendingDelete(row.slot); return; }
                          setPendingDelete(null);
                          props.onDelete(row.slot);
                        }}
                      >
                        {armed ? "Sure?" : "Delete"}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

            {/* What the two doors mean, set on the painting rather than as
                11px grey under the field it explains. The picture needs
                something to be about and this screen has exactly one thing to
                say that is not a row. */}
            <div className="hm-sl__aside">
              <div className="hm-cart">
                <span className="hm-cart__lead">
                  {mode === "save" ? "Where a save goes" : "Where a save comes from"}
                </span>
                <p className="hm-cart__note">
                  Slots live in this browser, and a browser gives the page about five
                  megabytes; a whole map is roughly one of them.
                </p>
                <p className="hm-cart__note">
                  {mode === "save"
                    ? "Save to File writes the game out as one file, which is the copy that survives a cleared browser."
                    : "Load from File takes a game written out earlier, which is the copy that survives a cleared browser."}
                </p>
              </div>
            </div>
          </div>

          {props.message !== null && props.message !== undefined && props.message !== "" ? (
            <div className="hs-parch hs-why" role="status">{props.message}</div>
          ) : null}
        </div>
        </PaintedPlate>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ styles

const SAVELOAD_STYLE_ID = "hm-saveload-art";

/* Mounted after the kit's sheets. No backticks below - template literal. */
export const SAVELOAD_CSS = `
/* This screen with nothing saved was a 680px card pinned to the top-left of a
   1920x1080 frame: 93% of the frame was one grey ramp, and the card that told
   the player there was nothing to load was the smallest thing on it. The column
   is centred in the frame rather than hung from the top, and it is wide enough
   that a save's name, its day and its size are one line each. */
/* The keyword "safe" matters: the content of this screen is taller than 768 with
   a long key list, and a plain centred flex item that overflows overflows in BOTH
   directions, so the top of the column goes above the frame where nothing can
   scroll it back. A safe centre centres when it fits and falls back to start
   when it does not. */
.hm-sl .hs-screen__body { align-items: center; align-items: safe center; }
.hm-sl__col {
  width: min(860px, 96%);
  max-height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
/* A list of saves is the whole screen when there is one; it gets the room. */
.hm-sl .hs-list { max-height: none; flex: 1 1 auto; }

/* ---- and when there is NOT one, it still gets the room.
   With nothing saved, this screen's content ended at row 598 of 1080 and left
   481px - 44.5% of the frame - of untouched backdrop under it, with the card
   that says there is nothing to load as the smallest thing on the screen. The
   door is the list; the list is the screen whether or not it has rows in it, so
   the panel is sized by the frame rather than by its contents and the sentence
   sits in the middle of it. 44.5% of a title-screen backdrop showing under a
   680px card is not a composition, it is a card that was never given a size. */
/* ---- the stage, and the register on it.
   The plate takes the whole body; the column is laid over it. The dialog is
   sized by its own rows and centred, so a screen with one save in it shows one
   save on a painting rather than one save at the top of a 700px ramp. */
.hm-sl__stage {
  flex: 1 1 auto;
  align-self: stretch;
  min-width: 0;
  min-height: 0;
}
.hm-root--phone .hm-sl__stage {
  flex: 0 0 auto;
  min-height: min-content;
}
.hm-sl .hm-plate__over { padding: 18px 22px; }
.hm-sl__col {
  max-height: 100%;
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 0.62fr);
  align-items: center;
  gap: 16px;
}
/* ---- the list and the picture, side by side.
   The previous shape gave the list a min-height of min(64vh, 700px) so that a
   screen with nothing saved was still a door rather than a card. That is right
   for the EMPTY state and wrong for every other one: with a single autosave in
   the index the rows occupied 40px of the 700 and the remaining 660 was a flat
   ramp - and because the floor is a viewport unit, it was a different amount of
   ramp at each size. Measured on the shots this replaces, the 32x32 blank scan
   read 29.1% at 1920x1080 and 38.8% at 1366x768: the same defect, two numbers,
   which is what a viewport-unit floor under fixed content gets you.
   Now the leftover is the painting, so there is no leftover to be flat. */
.hm-sl__spread { display: contents; }
.hm-sl__list {
  display: flex;
  flex-direction: column;
  min-height: 0;
  max-height: 100%;
}
/* The floor is for the EMPTY register only. A register with nothing entered in
   it has to be a page or it reads as a panel that failed to load; a register
   with one save in it does not, and giving that one a 460px floor is how the
   screen came to be one row of type at the top of a 660px ramp. Measured on
   the 1920x1080 shot with a single autosave in the index, that ramp was 14.7%
   of the frame by the coarse scan and it was the only thing the scan found. */
.hm-sl__list--empty { min-height: min(46vh, 460px); }
.hm-sl .hs-list { flex: 0 1 auto; }
.hm-sl__aside {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
}
@media (max-width: 1180px) {
  /* Under this the two columns are each too narrow for a save's name on one
     line, which is the thing the width was bought for. The note goes; the
     picture stays, because the picture is the screen. */
  .hm-sl__col { grid-template-columns: minmax(0, 1fr); }
  .hm-sl__aside { display: none; }
}

/* ---- the slot's own plate */
.hm-sl__seal {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 1px;
  width: 62px;
  padding: 3px 0 4px;
  background-image:
    radial-gradient(75% 60% at 50% 12%, rgba(255,226,176,0.14) 0%, rgba(0,0,0,0) 70%),
    linear-gradient(180deg, rgba(0,0,0,0.40) 0%, rgba(0,0,0,0.12) 55%, rgba(0,0,0,0.42) 100%);
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.85),
    inset 0 1px 0 rgba(255,232,186,0.16),
    inset 0 -1px 0 rgba(0,0,0,0.5);
}
.hm-sl__crest { margin-bottom: 1px; }
.hm-sl__sealword {
  font-family: var(--hm-font-display, 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif);
  font-size: max(9px, var(--hm-type-floor));
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #9c8760;
}

/* ---- its own ground, and why it is not the shared one.
   Settings and this screen are .hs-screen--sheet: they float on .hm-overlay
   at 0.55 and let what opened them show through, which is what the classic's own
   options and load dialogs do. That scrim is shared and it is not touched here:
   measured, it separates a panel from the title painting by 1.9x and from the
   adventure map by 2.6x, and it collapses to 1.0x over a town only because the
   town screen is 2.7x brighter than either - a number that belongs to the town,
   not to these two doors. What these two need is more separation over the TITLE
   screen, where "NEW GAME" and "QUIT" were legible straight through the panel
   edge and competed with it. So the extra darkening is here, on this screen's
   own ground, where it cannot cost the town screen anything.
   The grain is not decoration either: a flat wash is the one surface that
   measures as empty however dark it is. */
.hs-screen.hm-sl {
  background-color: transparent;
  background-image:
    radial-gradient(112% 90% at 50% 46%,
      rgba(6,8,12,0) 0%, rgba(6,8,12,0.20) 62%, rgba(4,5,9,0.44) 100%),
    radial-gradient(120% 90% at 50% -10%, rgba(90,74,44,0.20) 0%, rgba(0,0,0,0) 62%),
    linear-gradient(178deg,
      rgba(19,15,10,0.34) 0%, rgba(19,15,10,0.10) 42%, rgba(19,15,10,0.52) 100%);
}
/* Opaque, not a filter. A brightness(0.52) backdrop left the title's own type
   readable at metre height behind the dialog, which is a worse thing to look at
   than nothing; there is no exposure at which forty-point gilt serif under a
   panel reads as material. The grain stays - it is what stops the field being a
   surface with no cut in it - and the picture this screen needed is now an
   object on the screen rather than a ghost behind it. */
.hm-sl__veil {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background-color: #0d0906;
  background-image:
    repeating-linear-gradient(118deg,
      rgba(255,226,182,0.05) 0 2px,
      rgba(0,0,0,0.10) 2px 7px),
    repeating-linear-gradient(28deg,
      rgba(255,232,190,0.035) 0 1px,
      rgba(0,0,0,0.07) 1px 11px);
}

/* ---- the empty register */
.hm-sl__well {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  text-align: center;
  box-shadow: inset 0 1px 0 rgba(0,0,0,0.45), inset 0 0 40px rgba(0,0,0,0.28);
}
/* The two bands carry the ruling; the writing between them sits on clear
   ground. Each rules OUTWARD from the writing - the upper band's gradient runs
   bottom to top - so the halves are symmetric about the note instead of meeting
   at whatever phase the panel height happens to give them. */
.hm-sl__rule { flex: 1 1 auto; align-self: stretch; min-height: 0; }
/* A cut line, not a hairline. At 1px light over 1px dark on a 32px step the
   ruling was three luminance levels of standard deviation across a 32x32 block
   of panel - under the floor a blank-surface scan uses, and by eye a suggestion
   of a rule rather than a rule. Two pixels each way is what a scored line in
   this material looks like, and it measures six. */
.hm-sl__rule--over {
  background-image: repeating-linear-gradient(0deg,
    rgba(0,0,0,0) 0 28px,
    rgba(255,232,190,0.12) 28px 30px,
    rgba(0,0,0,0.20) 30px 32px);
}
.hm-sl__rule--under {
  background-image: repeating-linear-gradient(180deg,
    rgba(0,0,0,0) 0 28px,
    rgba(255,232,190,0.12) 28px 30px,
    rgba(0,0,0,0.20) 30px 32px);
}
.hm-sl__note {
  flex: 0 0 auto;
  width: min(820px, 92%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 11px;
  padding: 20px 26px 22px;
  background-color: #4b361d;
  background-image:
    repeating-linear-gradient(97deg,
      rgba(255,232,190,0.06) 0 1px,
      rgba(0,0,0,0.10) 1px 2px,
      rgba(0,0,0,0) 2px 6px),
    radial-gradient(90% 70% at 50% 0%, rgba(255,224,176,0.12) 0%, rgba(0,0,0,0) 66%),
    linear-gradient(178deg, #5f4526 0%, #4a3419 52%, #33230f 100%);
  box-shadow:
    inset 0 0 0 1px rgba(0,0,0,0.85),
    inset 0 0 0 2px rgba(216,178,96,0.24),
    inset 0 2px 0 rgba(255,232,186,0.10),
    0 10px 28px rgba(0,0,0,0.55);
}
.hm-sl__emptyhead {
  font-family: var(--hm-font-display, 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif);
  font-size: max(clamp(17px, 1.4vw, 24px), var(--hm-type-floor));
  letter-spacing: 0.05em;
  color: #e8d3a4;
  text-shadow: 0 2px 0 #241908, 0 3px 10px rgba(0,0,0,0.8);
}
.hm-sl__emptybody {
  max-width: 58ch;
  margin: 0;
  font-family: var(--hm-font-display, 'Palatino Linotype', 'Book Antiqua', Palatino, Georgia, serif);
  font-size: max(13px, var(--hm-type-floor));
  line-height: 1.6;
  color: #ddc89e;
  text-shadow: 0 1px 0 rgba(0,0,0,0.75);
}
.hm-sl__emptybody--second { color: #bfa87e; }
@media (max-height: 880px) {
  .hm-sl__well { gap: 9px; }
  .hm-sl__emptybody { font-size: max(12px, var(--hm-type-floor)); }
}
`;

const SAVELOAD_SHEET = `${FORM_ART_CSS}\n${SAVELOAD_CSS}`;

function SaveLoadStyles(): null {
  useStyleSheet(SAVELOAD_STYLE_ID, SAVELOAD_SHEET, StyleLayer.screen);
  return null;
}
